'use client';
import ReviewerFaceImg from './ReviewerFaceImg';
// ?layout=3.1–3.6 list renderers for keyword-lab topic landings (Founder order 01/10/26; spec m8/layouts-3x-spec.md by bezalel).
// Shared contract: no emoji, no ★, no side-border accents, no stacks of identical rounded cards; digits in <bdi dir="ltr">; reviewer =
// V5 (reviewCardName full name, "(n)" + tip, source + dd/MM/yy via ReviewSourceLine); every pro pictured, a reviewer only with a REAL photo.
// Contact = the SAME WaSmartButton / ContactButton doors (lead routing unchanged), only their `skin` differs.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import Link from 'next/link';
import WaSmartButton from './WaSmartButton';
import ContactButton from './ContactButton';
import ReviewerCountTip from './ReviewerCountTip';
import ReviewSourceLine from './ReviewSourceLine';
import ProViewBeacon from './ProViewBeacon';
import ReviewSlider, { ReviewSlide } from './ReviewSlider';
import type { ReviewItem } from './ReviewsList';
import type { ExpertItem } from './CategoryExpertsList';
import { fmtRating } from '@/lib/format';
import { avatarImg } from '@/lib/imgUrl';
import { reviewCardName, reviewerCountLabel, reviewSourceOf } from '@/lib/reviewsPreview';

const INK = '#0f172a', INK2 = '#334155', MUTE = '#64748b', HAIR = 'rgba(15,23,42,.10)', BLUE = '#1d4ed8', BLUE_INK = '#1e3a8a', WASH = '#eff4ff', WA = '#0f7a43';
const TAB: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

export type List3Variant = 'ledger' | 'today' | 'favorite' | 'sort' | 'sheet' | 'letters';
export type List3Props = {
  variant: List3Variant; experts: ExpertItem[]; nationwide: boolean;
  count: number; cityLabel: string | null; status: string | null; // status = "מאתר מומחים…" while the city resolves
  onChangeCity: () => void; coords: { lat: number; lng: number } | null;
  /** M3: THE shared sort control (components/ProSortTabs), state owned by CategoryExpertsList — `experts` arrive already sorted */
  sortBar?: ReactNode;
};
/** the shared sort control's row, right under each variant's count line (3.4 Sort keeps its own sticky placement) */
const SortRow = ({ p, mt = 10 }: { p: List3Props; mt?: number }) => p.sortBar ? <div data-sort-row style={{ margin: `${mt}px 0 4px` }}>{p.sortBar}</div> : null;

/* ── atoms ─────────────────────────────────────────────────────────────── */
const WaGlyph = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M12 2.6a9.3 9.3 0 0 0-8 14l-1.3 4.8 4.9-1.3A9.3 9.3 0 1 0 12 2.6Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9 7.6c.3-.2.7-.2.9.1l1 1.6c.2.3.1.6-.1.9l-.6.6c.6 1.2 1.6 2.2 2.8 2.8l.6-.6c.3-.2.6-.3.9-.1l1.6 1c.3.2.3.6.1.9-.6.9-1.6 1.3-2.6 1-2.8-.9-4.9-3-5.8-5.8-.3-1 .1-2 1-2.6Z" fill="currentColor" />
  </svg>
);
const PhoneGlyph = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

function Score({ v, size, of = 12, weight = 800, color = INK }: { v: number; size: number; of?: number; weight?: number; color?: string }) {
  return <bdi dir="ltr" style={{ ...TAB, whiteSpace: 'nowrap' }}><span style={{ fontSize: size, fontWeight: weight, color }}>{fmtRating(v)}</span><span style={{ fontSize: of, fontWeight: 500, color: MUTE }}>/5</span></bdi>; // raw Google rating out of 5 (Founder LAW 04/10/26)
}
const RevCount = ({ n, size = 12, style }: { n: number; size?: number; style?: CSSProperties }) => <span style={{ fontSize: size, fontWeight: 500, color: MUTE, whiteSpace: 'nowrap', ...style }}><bdi dir="ltr" style={TAB}>{n}</bdi> המלצות</span>;

function Face({ e, size, radius = '50%', ring = true, priority = false }: { e: ExpertItem; size: number | [number, number]; radius?: string | number; ring?: boolean; priority?: boolean }) {
  const [w, h] = Array.isArray(size) ? size : [size, size];
  return (
    <Link href={e.href} className="flex-shrink-0" aria-label={e.firstName} tabIndex={-1}>
      <img {...avatarImg(e.imgSrc, Math.max(w, h), { kind: 'pro', ctx: { category: e.categoryName, proName: e.firstName } })} width={w} height={h} loading={priority ? 'eager' : 'lazy'} decoding="async"
        style={{ width: w, height: h, borderRadius: radius, objectFit: 'cover', display: 'block', boxShadow: ring ? `0 0 0 1px ${HAIR}` : undefined }} />
    </Link>
  );
}
const Name = ({ e, size, as = 'h3' }: { e: ExpertItem; size: number; as?: 'h3' | 'span' }) => {
  const T = as;
  return <Link href={e.href} className="min-w-0 block"><T style={{ fontSize: size, fontWeight: size >= 20 ? 900 : 800, color: INK, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{e.firstName}</T></Link>;
};
const area = (e: ExpertItem, nationwide: boolean) => nationwide ? (e.locationStr ? `${e.locationStr} · בכל הארץ` : 'בכל הארץ') : (e.locationStr || '');

/** the pro's strongest written review (highest rating, then the longest text) */
function bestReview(e: ExpertItem): ReviewItem | null {
  const rs = (e.reviews as ReviewItem[]).filter(r => (r.review_text || '').trim());
  return rs.sort((a, b) => (Number(b.rating ?? 5) - Number(a.rating ?? 5)) || ((b.review_text || '').length - (a.review_text || '').length))[0] || null;
}
const writtenReviews = (e: ExpertItem) => (e.reviews as ReviewItem[]).filter(r => (r.review_text || '').trim());

function Byline({ r, size = 12, photo = false }: { r: ReviewItem; size?: number; photo?: boolean }) {
  const name = reviewCardName(r); const src = reviewSourceOf(r.review_source);
  const count = reviewerCountLabel(r.reviewer_google_review_count, src, name, r.reviewer_gender);
  return (
    <span className="flex items-center gap-1 min-w-0" style={{ fontSize: size, fontWeight: 500, color: MUTE }}>
      {photo && r.reviewer_avatar && <ReviewerFaceImg src={r.reviewer_avatar} name={name} size={20} fontSize={10} className="rounded-full object-cover flex-shrink-0" />}
      <span className="min-w-0 truncate" style={{ color: INK2, fontWeight: 600 }}>{name}</span>
      {count && <span className="flex-shrink-0"><ReviewerCountTip short={count.short} tip={count.tip} /></span>}
      <span aria-hidden="true">·</span>
      <ReviewSourceLine source={src} date={r.review_date} className="flex-shrink-0" style={{ fontSize: size }} />
    </span>
  );
}
const quoteText = (r: ReviewItem) => `״${(r.review_text || '').trim().replace(/\s+/g, ' ')}״`;
const clamp = (n: number): CSSProperties => ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

/* contact skins — the same doors, a typographic look */
type C = { e: ExpertItem };
const waProps = (e: ExpertItem) => ({ proArea: null, proName: e.fullName, proFirstName: e.firstName, proPhone: e.phone, whatsappValid: e.whatsappValid, proId: e.id, proGender: e.gender, categorySlug: e.categorySlug, categoryName: e.categoryName });
const callProps = (e: ExpertItem) => ({ proName: e.fullName, proFirstName: e.firstName, proPhone: e.phone, proId: e.id, proGender: e.gender, categorySlug: e.categorySlug, categoryName: e.categoryName });
const textAct: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '13px 8px', margin: '-13px 0', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' };
function TextActions({ e }: C) {
  return (
    <span className="inline-flex items-center flex-shrink-0">
      {e.phone && <WaSmartButton {...waProps(e)} skin={{ label: `וואטסאפ ל${e.firstName}`, style: { ...textAct, color: WA }, children: <><WaGlyph />וואטסאפ</> }} />}
      {e.phone && <span aria-hidden="true" style={{ width: 1, height: 14, background: HAIR }} />}
      <ContactButton {...callProps(e)} skin={{ label: `חיוג ל${e.firstName}`, style: { ...textAct, color: BLUE }, children: <>חיוג</> }} />
    </span>
  );
}
const circleBtn = (color: string, size = 40, filled = false): CSSProperties => ({ width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: filled ? 'none' : `1.5px solid ${color}`, background: filled ? color : '#fff', color: filled ? '#fff' : color, cursor: 'pointer', padding: 0, flexShrink: 0 });
function CircleActions({ e, vertical = false }: C & { vertical?: boolean }) {
  return (
    <span className={`flex flex-shrink-0 ${vertical ? 'flex-col' : ''}`} style={{ gap: 8 }}>
      {e.phone && <WaSmartButton {...waProps(e)} skin={{ label: `וואטסאפ ל${e.firstName}`, style: circleBtn(WA), children: <WaGlyph size={18} /> }} />}
      <ContactButton {...callProps(e)} skin={{ label: `חיוג ל${e.firstName}`, style: circleBtn(BLUE), children: <PhoneGlyph size={17} /> }} />
    </span>
  );
}
const linkBtn: CSSProperties = { background: 'none', border: 'none', padding: '10px 0', margin: '-10px 0', cursor: 'pointer', fontFamily: 'inherit', color: BLUE, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 };

function countWords(p: List3Props, prep: 'ל' | 'ב' | ' · ' = 'ל'): ReactNode {
  if (p.status) return p.status;
  const n = p.count === 1 ? <>מומחה <b style={{ fontWeight: 800 }}>אחד</b> זמין</> : <><b style={{ fontWeight: 800, ...TAB }}><bdi dir="ltr">{p.count}</bdi></b> מומחים זמינים</>;
  return <>{n}{p.cityLabel ? (prep === ' · ' ? ` · ${p.cityLabel}` : ` ${prep}${p.cityLabel}`) : ''}</>;
}
const changeLabel = (p: List3Props) => (p.cityLabel ? 'החלף עיר' : 'בחירת עיר');

/* ── 3.1 Ledger ────────────────────────────────────────────────────────── */
function Ledger(p: List3Props) {
  return (
    <div data-list3="ledger">
      <div className="flex items-center justify-between gap-3" style={{ marginTop: 12, padding: '8px 0', borderBottom: `1px solid ${INK}` }} data-count-line>
        <p className="min-w-0 truncate" style={{ fontSize: 14, fontWeight: 500, color: INK, margin: 0 }}>{countWords(p)}</p>
        <button type="button" onClick={p.onChangeCity} style={{ ...linkBtn, fontSize: 14, textDecoration: 'underline', textUnderlineOffset: 2 }}>{changeLabel(p)}</button>
      </div>
      <SortRow p={p} />
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {p.experts.map((e, i) => { const r = bestReview(e); return (
          <li key={e.id} data-pro-row style={{ display: 'grid', gridTemplateColumns: '22px 64px minmax(0,1fr) auto', columnGap: 10, rowGap: 6, padding: '14px 0', borderBottom: `1px solid ${HAIR}` }}>
            <span style={{ ...TAB, fontSize: 13, fontWeight: 700, color: MUTE, paddingTop: 2 }}><ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} /><bdi dir="ltr">{String(i + 1).padStart(2, '0')}</bdi></span>
            <Face e={e} size={64} priority={i === 0} />
            <div className="min-w-0" style={{ alignSelf: 'center' }}><Name e={e} size={17} /><p className="truncate" style={{ fontSize: 13, color: MUTE, margin: '2px 0 0' }}>{area(e, p.nationwide)}</p></div>
            <div style={{ textAlign: 'end' }}><Score v={e.rating10} size={20} /><div><RevCount n={e.reviewCount} /></div><div style={{ marginTop: 6 }}><TextActions e={e} /></div></div>
            <div style={{ gridColumn: '3 / 5' }} className="min-w-0">
              {r && <p style={{ ...clamp(1), fontSize: 14, color: INK2, margin: 0 }}>{quoteText(r)}</p>}
              {r && <div className="min-w-0 overflow-hidden" style={{ marginTop: 4 }}><Byline r={r} /></div>}
            </div>
          </li>
        ); })}
      </ol>
    </div>
  );
}

/* ── 3.2 Today ─────────────────────────────────────────────────────────── */
function Today(p: List3Props) {
  const [lead, ...rest] = p.experts; const r = lead ? bestReview(lead) : null;
  return (
    <div data-list3="today">
      <div className="flex items-center justify-between gap-3" style={{ borderTop: `1px solid ${INK}`, padding: '8px 0' }} data-count-line>
        <p className="min-w-0 truncate" style={{ fontSize: 13, fontWeight: 700, color: INK, margin: 0 }}>{p.status || <><bdi dir="ltr" style={TAB}>{p.count}</bdi> מומחים{p.cityLabel ? ` · ${p.cityLabel}` : ''}</>}</p>
        <button type="button" onClick={p.onChangeCity} style={{ ...linkBtn, fontSize: 13 }}>{changeLabel(p)}</button>
      </div>
      <SortRow p={p} mt={4} />
      {lead && (
        <article data-pro-row style={{ margin: '4px -16px 0', padding: '16px', background: '#fff', borderBottom: `1px solid ${HAIR}` }}>
          <ProViewBeacon id={lead.id} name={lead.firstName} categorySlug={lead.categorySlug} />
          <p style={{ fontSize: 11, fontWeight: 800, color: BLUE_INK, margin: '0 0 10px', letterSpacing: '.02em' }}>ראשון בדירוג{p.cityLabel ? ` ל${p.cityLabel}` : ''}</p>
          <div className="flex gap-3">
            <Face e={lead} size={[110, 130]} radius={6} priority />
            <div className="min-w-0 flex-1" style={{ alignSelf: 'center' }}>
              <Name e={lead} size={24} />
              <div className="flex items-baseline gap-2" style={{ marginTop: 4 }}><Score v={lead.rating10} size={15} of={11} weight={700} /><RevCount n={lead.reviewCount} size={13} /></div>
              <p className="truncate" style={{ fontSize: 13, color: MUTE, margin: '3px 0 0' }}>{area(lead, p.nationwide)}</p>
            </div>
          </div>
          {r && (
            <blockquote style={{ position: 'relative', margin: '14px 0 0', paddingInlineStart: 0 }}>
              <span aria-hidden="true" style={{ position: 'absolute', insetInlineStart: -14, top: -6, fontSize: 34, fontWeight: 900, color: BLUE, lineHeight: 1 }}>״</span>
              <p style={{ ...clamp(3), fontSize: 17, fontWeight: 500, lineHeight: 1.45, color: INK, margin: 0 }}>{(r.review_text || '').trim()}״</p>
              <footer style={{ marginTop: 6 }}><Byline r={r} photo /></footer>
            </blockquote>
          )}
          <div className="flex" style={{ marginTop: 14, height: 44, borderRadius: 10, overflow: 'hidden', boxShadow: `inset 0 0 0 1px ${HAIR}` }}>
            {lead.phone && <WaSmartButton {...waProps(lead)} skin={{ label: `וואטסאפ ל${lead.firstName}`, style: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: WA, color: '#fff', border: 'none', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }, children: <><WaGlyph />וואטסאפ</> }} />}
            <ContactButton {...callProps(lead)} skin={{ label: `חיוג ל${lead.firstName}`, style: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', color: BLUE, border: 'none', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }, children: <><PhoneGlyph />חיוג</> }} />
          </div>
        </article>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rest.map(e => (
          <li key={e.id} data-pro-row className="flex items-center gap-3" style={{ minHeight: 72, borderBottom: `1px solid ${HAIR}`, padding: '10px 0' }}>
            <Face e={e} size={56} />
            <div className="min-w-0 flex-1"><ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
              <div className="flex items-baseline gap-2 min-w-0"><Name e={e} size={16} /><Score v={e.rating10} size={14} of={10} /></div>
              <p className="truncate" style={{ fontSize: 12.5, color: MUTE, margin: '2px 0 0' }}>{area(e, p.nationwide)} · <RevCount n={e.reviewCount} size={12.5} /></p>
            </div>
            <CircleActions e={e} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── 3.3 Guest Favorite ────────────────────────────────────────────────── */
const LAUREL_RULE = (e: ExpertItem) => e.rating10 >= 4.8 && e.reviewCount >= 20; // raw Google 1-5 (was 9.5/10) // the stated rule — never a free claim
function Laurel({ flip = false }: { flip?: boolean }) {
  return (
    <svg width="13" height="32" viewBox="0 0 13 32" aria-hidden="true" style={{ flexShrink: 0, transform: flip ? 'scaleX(-1)' : undefined }}>
      <path d="M10 31C4 26 2.5 17 6 4" fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
      {[[5.2, 8, -40], [4, 13.5, -25], [3.6, 19, -10], [4.6, 24.5, 8]].map(([x, y, a], i) => (
        <ellipse key={i} cx={x} cy={y} rx="1.6" ry="3.4" transform={`rotate(${a} ${x} ${y}) translate(-2.2 0)`} fill="none" stroke={INK} strokeWidth="1.3" />
      ))}
    </svg>
  );
}
function Favorite(p: List3Props) {
  return (
    <div data-list3="favorite">
      <div className="flex items-center" style={{ margin: '12px 0 4px', height: 48, borderRadius: 999, boxShadow: `0 0 0 1px ${HAIR}, 0 2px 8px rgba(15,23,42,.08)`, padding: '0 6px 0 6px', background: '#fff' }} data-count-line>
        <span style={{ width: 36, flexShrink: 0 }} />
        <button type="button" onClick={p.onChangeCity} className="flex-1 min-w-0" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', padding: 0 }} aria-label={`${p.cityLabel || 'בחירת עיר'} — ${changeLabel(p)}`}>
          <span className="block truncate" style={{ fontSize: 14, fontWeight: 800, color: INK }}>{p.cityLabel || 'כל הארץ'}</span>
          <span className="block truncate" style={{ fontSize: 12, fontWeight: 500, color: MUTE }}>{p.status || <><bdi dir="ltr" style={TAB}>{p.count}</bdi> מומחים זמינים</>}</span>
        </button>
        <button type="button" onClick={p.onChangeCity} aria-label={changeLabel(p)} title={changeLabel(p)} style={{ ...circleBtn(BLUE, 36, true) }}>
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4v16M7 4 3 8M7 4l4 4M17 20V4M17 20l-4-4M17 20l4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <SortRow p={p} mt={8} />
      {p.experts.map((e, i) => { const rs = writtenReviews(e).slice(0, 8); return (
        <section key={e.id} data-pro-row aria-label={e.firstName} style={{ padding: '20px 0', borderBottom: `1px solid ${HAIR}` }}>
          <ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
          <div className="flex gap-3" style={{ alignItems: 'flex-start' }}>
            <Face e={e} size={70} priority={i === 0} />
            <div className="min-w-0 flex-1" style={{ paddingTop: 10 }}><Name e={e} size={18} /><p className="truncate" style={{ fontSize: 13, color: MUTE, margin: '2px 0 0' }}>{area(e, p.nationwide)}</p></div>
            <div className="flex flex-col items-center flex-shrink-0" style={{ gap: 6 }}>
              <div className="flex items-center" style={{ gap: 3 }} aria-label={LAUREL_RULE(e) ? `דירוג ${fmtRating(e.rating10)} מתוך 5 — אהוב על לקוחות` : undefined}>
                {LAUREL_RULE(e) && <Laurel />}
                <bdi dir="ltr" style={{ ...TAB, fontSize: 22, fontWeight: 800, color: INK }}>{fmtRating(e.rating10)}</bdi>
                {LAUREL_RULE(e) && <Laurel flip />}
              </div>
              <Link href={`${e.href}#reviews`} style={{ fontSize: 11, fontWeight: 600, color: INK2, textDecoration: 'underline', textUnderlineOffset: 2, marginTop: -6 }}><bdi dir="ltr" style={TAB}>{e.reviewCount}</bdi> המלצות</Link>
              <span className="flex items-center gap-2">
                {e.phone && <WaSmartButton {...waProps(e)} skin={{ label: `וואטסאפ ל${e.firstName}`, style: { display: 'inline-flex', alignItems: 'center', gap: 5, height: 36, padding: '0 12px', borderRadius: 999, background: WA, color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }, children: <><WaGlyph size={15} />וואטסאפ</> }} />}
                <ContactButton {...callProps(e)} skin={{ label: `חיוג ל${e.firstName}`, style: { ...textAct, color: BLUE, padding: '11px 2px', margin: '-11px 0' }, children: <>חיוג</> }} />
              </span>
            </div>
          </div>
          {rs.length > 0 && (
            <ReviewSlider proId={e.id} bleed style={{ marginTop: 12 }}>
              {rs.map((r, k) => (
                <ReviewSlide key={k} style={{ alignSelf: 'stretch', minHeight: 88, borderRadius: 12, boxShadow: `inset 0 0 0 1px ${HAIR}`, padding: '10px 12px', background: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 6 }}>
                  <p style={{ ...clamp(2), fontSize: 13.5, color: INK, margin: 0, lineHeight: 1.4 }}>{quoteText(r)}</p>
                  <Byline r={r} size={11.5} />
                </ReviewSlide>
              ))}
            </ReviewSlider>
          )}
        </section>
      ); })}
    </div>
  );
}

/* ── 3.4 Sort ──────────────────────────────────────────────────────────── */
// M3: the 3-option local sort (rank/near/reviews) is replaced by THE shared 2-option control + state (components/ProSortTabs);
// this variant keeps only its sticky placement — `p.experts` arrive already sorted.
function Sort(p: List3Props) {
  const [stuck, setStuck] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // pinned while the list is on screen: from the control's own spot until the list's end (not over the price block below)
    const on = () => { const s = sentinel.current, e = listEnd.current; if (!s || !e) return; setStuck(s.getBoundingClientRect().top < 0 && e.getBoundingClientRect().top > 60); };
    on(); window.addEventListener('scroll', on, { passive: true }); window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, []);
  const list = p.experts;
  const faces = p.experts.slice(0, 5); const more = Math.max(0, p.count - faces.length);
  return (
    <div data-list3="sort">
      <div className="flex items-center gap-2" style={{ margin: '12px 0 8px', minHeight: 36 }} data-count-line>
        <span className="flex flex-shrink-0" aria-hidden="true">
          {faces.map((e, i) => <img key={e.id} {...avatarImg(e.imgSrc, 28)} alt="" width={28} height={28} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px #fff', marginInlineStart: i ? -8 : 0, position: 'relative', zIndex: 5 - i }} />)}
          {more > 0 && <span style={{ width: 28, height: 28, borderRadius: '50%', background: WASH, color: BLUE_INK, fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginInlineStart: -8, boxShadow: '0 0 0 2px #fff', position: 'relative' }}><bdi dir="ltr" style={TAB}>+{more}</bdi></span>}
        </span>
        <p className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 700, color: INK, margin: 0 }}>{countWords(p)}</p>
        <button type="button" onClick={p.onChangeCity} style={{ ...linkBtn, fontSize: 13 }}>{changeLabel(p)}</button>
      </div>
      <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />
      {p.sortBar && <div style={{ height: stuck ? 48 : undefined }}>{/* keeps the flow height while the control is pinned */}
      <div data-sort-bar style={stuck
        ? { position: 'fixed', top: 0, insetInline: 0, zIndex: 30, background: '#fff', padding: '6px 16px', borderBottom: `1px solid ${HAIR}` }
        : { position: 'relative', background: '#fff', margin: '0 -16px', padding: '6px 16px', borderBottom: '1px solid transparent' }}>
        <div style={{ maxWidth: stuck ? 992 : undefined, margin: stuck ? '0 auto' : undefined }}>{p.sortBar}</div>
      </div>
      </div>}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-live="polite">
        {list.map((e, i) => { const r = bestReview(e); return (
          <li key={e.id} data-pro-row style={{ padding: '14px 0', borderBottom: `1px solid ${HAIR}` }}>
            <ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
            <div className="flex gap-3">
              <Face e={e} size={80} radius={20} priority={i === 0} />
              <div className="min-w-0 flex-1">
                <Name e={e} size={17} />
                <div className="flex items-baseline gap-1 flex-wrap" style={{ marginTop: 2 }}><Score v={e.rating10} size={15} of={11} /><span style={{ color: MUTE, fontSize: 13 }}>·</span><RevCount n={e.reviewCount} size={13} /></div>
                <p className="truncate" style={{ fontSize: 13, color: MUTE, margin: '1px 0 0' }}>{area(e, p.nationwide)}</p>
              </div>
              <CircleActions e={e} vertical />
            </div>
            {r && <div style={{ marginTop: 8 }}><p style={{ ...clamp(2), fontSize: 14, color: INK2, margin: 0, lineHeight: 1.45 }}>{quoteText(r)}</p><div style={{ marginTop: 3 }}><Byline r={r} /></div></div>}
          </li>
        ); })}
      </ul>
      <div ref={listEnd} aria-hidden="true" style={{ height: 1 }} />
    </div>
  );
}

/* ── 3.5 Sheet ─────────────────────────────────────────────────────────── */
function Sheet(p: List3Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const drag = useRef<{ y: number; y0: number; moved: boolean } | null>(null);
  const snap = (toFull: boolean) => {
    const el = ref.current; if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: toFull ? top : 0, behavior: 'smooth' });
    setFull(toFull);
  };
  useEffect(() => {
    const on = () => { const el = ref.current; if (el) setFull(el.getBoundingClientRect().top <= 2); };
    window.addEventListener('scroll', on, { passive: true }); return () => window.removeEventListener('scroll', on);
  }, []);
  return (
    <div ref={ref} data-list3="sheet" style={{ position: 'relative', margin: '-20px -16px 0', background: '#fff', borderRadius: '20px 20px 0 0', boxShadow: '0 -8px 24px rgba(15,23,42,.12)', padding: '0 16px' }}>
      <button type="button" aria-label={full ? 'כיווץ הרשימה' : 'הרחבת הרשימה'} aria-expanded={full} data-sheet-grabber
        onClick={() => { if (!drag.current?.moved) snap(!full); }}
        onPointerDown={ev => { drag.current = { y: ev.clientY, y0: ev.clientY, moved: false }; (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId); }}
        onPointerMove={ev => { const d = drag.current; if (!d) return; const dy = ev.clientY - d.y; if (Math.abs(dy) > 6) { d.moved = true; window.scrollBy(0, -dy); d.y = ev.clientY; } }}
        onPointerUp={ev => { const d = drag.current; if (d?.moved) { const total = ev.clientY - d.y0; snap(total < 0); } setTimeout(() => { drag.current = null; }, 0); }}
        style={{ display: 'block', width: '100%', height: 24, background: 'none', border: 'none', cursor: 'grab', touchAction: 'none', padding: 0 }}>
        <span aria-hidden="true" style={{ display: 'block', width: 36, height: 4, borderRadius: 2, background: '#cbd5e1', margin: '10px auto 0' }} />
      </button>
      <div className="flex items-center justify-between gap-3" style={{ minHeight: 44, borderBottom: `1px solid ${HAIR}`, paddingBottom: 8 }} data-count-line>
        <p className="min-w-0 truncate" style={{ fontSize: 17, fontWeight: 800, color: INK, margin: 0 }}>{p.status || <>{p.count === 1 ? 'מומחה אחד זמין' : <><bdi dir="ltr" style={TAB}>{p.count}</bdi> מומחים זמינים</>}{p.cityLabel ? ` ל${p.cityLabel}` : ''}</>}</p>
        <button type="button" onClick={p.onChangeCity} style={{ flexShrink: 0, height: 32, padding: '0 12px', borderRadius: 999, border: 'none', boxShadow: `inset 0 0 0 1px ${HAIR}`, background: '#fff', color: INK, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>{changeLabel(p)}</button>
      </div>
      <SortRow p={p} />
      <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0 0' }}>
        {p.experts.map((e, i) => { const r = bestReview(e); const on = sel === e.id; return (
          <li key={e.id} data-pro-row data-selected={on ? '1' : undefined}
            onClick={ev => { if ((ev.target as HTMLElement).closest('a,button')) return; setSel(on ? null : e.id); }}
            style={{ padding: '12px 10px', margin: '4px -10px', borderRadius: 12, boxShadow: on ? `0 0 0 2px ${INK}` : 'none', borderBottom: on ? 'none' : `1px solid ${HAIR}`, cursor: 'pointer' }}>
            <ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
            <div className="flex items-center gap-3">
              <Face e={e} size={64} priority={i === 0} />
              <div className="min-w-0 flex-1">
                <Name e={e} size={16} />
                <p className="truncate" style={{ fontSize: 13, fontWeight: 600, color: INK2, margin: '2px 0 0' }}><Score v={e.rating10} size={13} of={11} weight={600} color={INK2} /> · <RevCount n={e.reviewCount} size={13} style={{ fontWeight: 600 }} /></p>
                <p className="truncate" style={{ fontSize: 12.5, color: MUTE, margin: '1px 0 0' }}>{area(e, p.nationwide)}</p>
              </div>
              <span className="flex items-center flex-shrink-0" style={{ gap: 4 }}>
                <ContactButton {...callProps(e)} skin={{ label: `חיוג ל${e.firstName}`, style: { ...circleBtn(BLUE, 40), border: 'none', background: 'transparent' }, children: <PhoneGlyph size={18} /> }} />
                {e.phone && <WaSmartButton {...waProps(e)} skin={{ label: `וואטסאפ ל${e.firstName}`, style: circleBtn(WA, 44, true), children: <WaGlyph size={20} /> }} />}
              </span>
            </div>
            {r && <div style={{ marginTop: 6, paddingInlineStart: 76 }}><p className="truncate" style={{ fontSize: 13.5, color: INK2, margin: 0 }}>{quoteText(r)}</p><Byline r={r} size={11.5} /></div>}
          </li>
        ); })}
      </ul>
    </div>
  );
}

/* ── 3.6 Letters ───────────────────────────────────────────────────────── */
function Letters(p: List3Props) {
  return (
    <div data-list3="letters">
      <div className="flex items-center gap-2" style={{ margin: '12px 0 0', padding: '6px 0 8px', borderBottom: `1px solid ${INK}` }} data-count-line>
        <p className="min-w-0 truncate" style={{ fontSize: 14, fontWeight: 700, color: INK, margin: 0 }}>{p.status || <>{p.count === 1 ? 'מומחה אחד' : <><bdi dir="ltr" style={TAB}>{p.count}</bdi> מומחים</>}{p.cityLabel ? ` ב${p.cityLabel}` : ''}</>}</p>
        <span aria-hidden="true" style={{ color: MUTE }}>·</span>
        <button type="button" onClick={p.onChangeCity} style={{ ...linkBtn, fontSize: 14 }}>{changeLabel(p)}</button>
      </div>
      <SortRow p={p} />
      {p.experts.map((e, i) => { const r = bestReview(e); const lead = i === 0; return (
        <article key={e.id} data-pro-row style={{ padding: '16px 0 14px', borderBottom: `1px solid ${HAIR}` }}>
          <ProViewBeacon id={e.id} name={e.firstName} categorySlug={e.categorySlug} />
          {r && (
            <blockquote style={{ position: 'relative', margin: 0, paddingInlineStart: 14 }}>
              <span aria-hidden="true" style={{ position: 'absolute', insetInlineStart: -2, top: lead ? -8 : -6, fontSize: 44, fontWeight: 900, color: BLUE, lineHeight: 1 }}>״</span>
              <p style={{ ...clamp(lead ? 3 : 2), fontSize: lead ? 19 : 16, fontWeight: 500, lineHeight: 1.45, color: INK, margin: 0 }}>{(r.review_text || '').trim()}״</p>
              <footer style={{ marginTop: 6 }}><Byline r={r} photo /></footer>
            </blockquote>
          )}
          <div className="flex items-center gap-2 min-w-0" style={{ marginTop: r ? 10 : 0, paddingInlineStart: r ? 14 : 0 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: MUTE }}>על</span>
            <Face e={e} size={50} priority={lead} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5 min-w-0"><Name e={e} size={15} /><Score v={e.rating10} size={13} of={10} weight={700} /></div>
              <RevCount n={e.reviewCount} />
            </div>
            <TextActions e={e} />
          </div>
        </article>
      ); })}
    </div>
  );
}

// M3: the old .tl3-snap strip CSS is gone — reviews ride THE shared slider (components/ReviewSlider, CSS in app/globals.css).

export default function TopicList3(p: List3Props) {
  return (
    <div className="min-w-0">
      {p.variant === 'ledger' ? <Ledger {...p} /> : p.variant === 'today' ? <Today {...p} /> : p.variant === 'favorite' ? <Favorite {...p} />
        : p.variant === 'sort' ? <Sort {...p} /> : p.variant === 'sheet' ? <Sheet {...p} /> : <Letters {...p} />}
    </div>
  );
}
